/**
 * LDS/HYP payment outcome routes (post-callback FE landing pages).
 * Read-only display — never triggers payment capture on refresh.
 */
import { getAgentDashboardLdsPaymentPath } from "./orders/resolveAgentLdsFlightsHotelPaymentNav.js";

export const LDS_HYP_PAYMENT_RETURN_TO_STORAGE_PREFIX = "lds_hyp_payment_return_to:";
/** Global session key — last LDS payment page URL before HYP redirect. */
export const LAST_LDS_PAYMENT_URL_SESSION_KEY = "lastLdsPaymentUrl";

function trimReturnPath(path) {
  const t = String(path || "").trim();
  if (!t || !t.startsWith("/")) return null;
  if (t.includes("://")) return null;
  return t.split("#")[0];
}

function isDevRuntime() {
  if (typeof import.meta !== "undefined" && import.meta?.env?.DEV) return true;
  if (typeof process !== "undefined" && process?.env?.NODE_ENV) {
    return process.env.NODE_ENV !== "production";
  }
  return false;
}

function emitLdsSuccessReturnNavTrace(payload) {
  if (!isDevRuntime()) return;
  try {
    // eslint-disable-next-line no-console
    console.info("[LDS_SUCCESS_RETURN_NAV_TRACE]", payload);
  } catch {
    /* ignore */
  }
}

/**
 * Agent LDS package payment page — matches Routes.jsx `path="payment/:currentOrderId"`.
 * @param {string|null|undefined} orderId
 * @param {object|null|undefined} [orderRow]
 * @returns {string|null}
 */
export function resolveLdsHypPaymentPagePath(orderId, orderRow = null) {
  const fromHelper = getAgentDashboardLdsPaymentPath(orderId, orderRow);
  if (fromHelper) return fromHelper;
  const oid = String(orderId || "").trim();
  if (!oid) return null;
  return `/p/dashboard/payment/${encodeURIComponent(oid)}`;
}

/**
 * @param {string|null|undefined} path
 * @param {string|null|undefined} [orderId]
 */
export function isLdsPaymentPagePath(path, orderId = null) {
  const p = trimReturnPath(path);
  if (!p) return false;
  const pathOnly = p.split("?")[0];
  if (!pathOnly.startsWith("/p/dashboard/payment/")) return false;
  if (!orderId) return true;
  const oid = encodeURIComponent(String(orderId).trim());
  return pathOnly === `/p/dashboard/payment/${oid}` || pathOnly.includes(`/payment/${oid}`);
}

/**
 * Persist payment page URL before leaving for HYP (survives redirect).
 * @param {string|null|undefined} orderId
 * @param {string|null|undefined} returnPath
 */
export function persistLdsHypPaymentReturnTo(orderId, returnPath) {
  if (typeof sessionStorage === "undefined") return;
  const oid = String(orderId || "").trim();
  const path = trimReturnPath(returnPath);
  if (!oid || !path) return;
  try {
    sessionStorage.setItem(`${LDS_HYP_PAYMENT_RETURN_TO_STORAGE_PREFIX}${oid}`, path);
    if (isLdsPaymentPagePath(path, oid)) {
      sessionStorage.setItem(LAST_LDS_PAYMENT_URL_SESSION_KEY, path);
    }
  } catch {
    /* ignore quota / private mode */
  }
}

/**
 * @param {string|null|undefined} orderId
 * @returns {string|null}
 */
export function readLdsHypPaymentReturnTo(orderId) {
  if (typeof sessionStorage === "undefined") return null;
  const oid = String(orderId || "").trim();
  if (!oid) return null;
  try {
    return trimReturnPath(
      sessionStorage.getItem(`${LDS_HYP_PAYMENT_RETURN_TO_STORAGE_PREFIX}${oid}`)
    );
  } catch {
    return null;
  }
}

/**
 * @param {string|null|undefined} orderId
 * @returns {string|null}
 */
export function readLastLdsPaymentUrlFromSession(orderId) {
  if (typeof sessionStorage === "undefined") return null;
  try {
    const globalUrl = trimReturnPath(
      sessionStorage.getItem(LAST_LDS_PAYMENT_URL_SESSION_KEY)
    );
    if (globalUrl && isLdsPaymentPagePath(globalUrl, orderId)) {
      return globalUrl;
    }
    const perOrder = readLdsHypPaymentReturnTo(orderId);
    if (perOrder && isLdsPaymentPagePath(perOrder, orderId)) {
      return perOrder;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * @param {URLSearchParams|{ get?: (k: string) => string|null }} searchParams
 */
function pickReturnPathFromQuery(searchParams) {
  if (!searchParams || typeof searchParams.get !== "function") return null;
  for (const key of ["returnTo", "return_to", "sourceUrl", "source_url"]) {
    const hit = trimReturnPath(searchParams.get(key));
    if (hit) return hit;
  }
  return null;
}

export function getLdsHypPaymentSuccessPath(orderId) {
  const oid = String(orderId || "").trim();
  if (!oid) return "/payment/success/lds";
  return `/payment/success/lds/${encodeURIComponent(oid)}`;
}

export function getLdsHypPaymentFailPath(orderId) {
  const oid = String(orderId || "").trim();
  if (!oid) return "/payment/fail/lds";
  return `/payment/fail/lds/${encodeURIComponent(oid)}`;
}

export function getLdsHypPaymentCancelPath(orderId) {
  const oid = String(orderId || "").trim();
  if (!oid) return "/payment/cancel/lds";
  return `/payment/cancel/lds/${encodeURIComponent(oid)}`;
}

/**
 * Legacy BE redirect landed on /?status=...&orderType=LDS&orderId=...
 * @param {URLSearchParams} searchParams
 * @returns {string|null}
 */
export function resolveLegacyLdsHypPaymentHomeRedirect(searchParams) {
  if (!searchParams || typeof searchParams.get !== "function") return null;
  const orderId = String(searchParams.get("orderId") || "").trim();
  if (!orderId) return null;
  const orderType = String(searchParams.get("orderType") || "").trim().toUpperCase();
  if (orderType !== "LDS" && orderType !== "TRUE") return null;

  const status = String(searchParams.get("status") || "success").trim().toLowerCase();
  const checkoutStatus = String(searchParams.get("checkoutStatus") || "").trim();

  let basePath;
  if (status === "fail" || status === "failure" || status === "failed") {
    basePath = getLdsHypPaymentFailPath(orderId);
  } else if (status === "cancel") {
    basePath = getLdsHypPaymentCancelPath(orderId);
  } else {
    basePath = getLdsHypPaymentSuccessPath(orderId);
  }

  const q = new URLSearchParams();
  q.set("orderType", "LDS");
  if (checkoutStatus) q.set("checkoutStatus", checkoutStatus);
  if (status && status !== "success" && status !== "fail" && status !== "cancel") {
    q.set("status", status);
  } else if (basePath.includes("/success/") && status === "partial") {
    q.set("status", "partial");
  }

  const qs = q.toString();
  return qs ? `${basePath}?${qs}` : basePath;
}

export function resolveLdsHypOrderDetailsPath(orderId, { isAgentSession = false } = {}) {
  const oid = encodeURIComponent(String(orderId || "").trim());
  if (!oid) return "/";
  if (isAgentSession) {
    return `/p/dashboard/orders/packages/${oid}`;
  }
  return `/packages/profile/${oid}`;
}

/**
 * Partial-payment success — "חזרה לפרטי ההזמנה" returns to LDS payment page when possible.
 * @param {string|null|undefined} orderId
 * @param {URLSearchParams|{ get?: (k: string) => string|null }} [searchParams]
 * @param {{
 *   isAgentSession?: boolean,
 *   orderRow?: object|null,
 *   status?: string|null,
 *   orderType?: string|null,
 * }} [options]
 * @returns {string}
 */
export function resolveLdsHypPartialSuccessReturnPath(
  orderId,
  searchParams = null,
  { isAgentSession = false, orderRow = null, status = null, orderType = null } = {}
) {
  const oid = String(orderId || "").trim();
  const statusNorm = String(status ?? searchParams?.get?.("status") ?? "")
    .trim()
    .toLowerCase();
  const orderTypeNorm = String(orderType ?? searchParams?.get?.("orderType") ?? "")
    .trim()
    .toUpperCase();
  const isPartialContext =
    statusNorm === "partial" ||
    orderTypeNorm === "LDS" ||
    orderTypeNorm === "TRUE";

  const returnToQuery = pickReturnPathFromQuery(searchParams);
  const sessionReturnTo = readLastLdsPaymentUrlFromSession(oid);
  const paymentPagePath = resolveLdsHypPaymentPagePath(oid, orderRow);

  let selectedRoute = "/";
  let fallbackUsed = "none";

  if (returnToQuery && isLdsPaymentPagePath(returnToQuery, oid)) {
    selectedRoute = returnToQuery;
    fallbackUsed = "returnTo_query_payment";
  } else if (sessionReturnTo) {
    selectedRoute = sessionReturnTo;
    fallbackUsed = "sessionStorage_payment";
  } else if (paymentPagePath) {
    selectedRoute = paymentPagePath;
    fallbackUsed = "lds_payment_page_path";
  } else if (isAgentSession) {
    selectedRoute = resolveLdsHypOrderDetailsPath(oid, { isAgentSession: true });
    fallbackUsed = "order_details_agent";
  } else {
    selectedRoute = resolveLdsHypOrderDetailsPath(oid, { isAgentSession: false });
    fallbackUsed = "order_details_customer";
  }

  if (isPartialContext) {
    emitLdsSuccessReturnNavTrace({
      orderId: oid,
      status: statusNorm || null,
      returnToQuery: returnToQuery || null,
      sessionReturnTo: sessionReturnTo || null,
      selectedRoute,
      fallbackUsed,
      isAgentSession,
      paymentPagePath: paymentPagePath || null,
    });
  }

  return selectedRoute;
}
